import { zodResolver } from "@hookform/resolvers/zod";
import { AuthButton, Box, MarkdownRenderer } from "@memorang/ui";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TextInput as RNTextInput } from "react-native";
import { HelperText, Text, TextInput } from "react-native-paper";
import { z } from "zod";

interface Props {
	loading?: boolean;
	handleSubmitEmail: (email: string, password?: string) => void;
	emailFromUrl?: string;
	lastLoggedInEmail?: string;
	tos?: string;
	privacy?: string;
	showPasswordField?: boolean;
	onChangeEmail?: (email: string) => void;
}

const emailSchema = z.object({
	email: z.string().email("Please enter a valid email address"),
	password: z.string().optional(),
});

const EmailForm = ({
	loading,
	handleSubmitEmail,
	emailFromUrl,
	lastLoggedInEmail,
	tos,
	privacy,
	showPasswordField = false,
	onChangeEmail,
}: Props) => {
	const {
		control,
		handleSubmit,
		watch,
		formState: { errors },
		setValue,
	} = useForm({
		resolver: zodResolver(emailSchema),
		mode: "onSubmit",
	});

	const { t } = useTranslation();
	const passwordInputRef = useRef<RNTextInput | null>(null);
	const [isPasswordHidden, setIsPasswordHidden] = useState(true);

	const watchEmail = watch("email");
	const watchPassword = watch("password");

	useEffect(() => {
		if (emailFromUrl || lastLoggedInEmail) {
			setValue("email", emailFromUrl || lastLoggedInEmail);
		}
	}, [emailFromUrl, setValue, lastLoggedInEmail]);

	const footerText = `By clicking "Continue" you agree to our [Terms](${tos}) and [Privacy Policy](${privacy}).`;

	const emailError =
		errors.email && typeof errors.email.message === "string"
			? errors.email.message
			: null;
	const passwordError =
		errors.password && typeof errors.password.message === "string"
			? errors.password.message
			: null;

	const Footer = () => (
		<MarkdownRenderer
			text={footerText}
			variant="explanation"
			textAlign="center"
			useCustomLink={false}
		/>
	);

	return (
		<Box gap={24} width="100%">
			{lastLoggedInEmail && (
				<Box paddingHorizontal={16}>
					<MarkdownRenderer
						overrideRules={{
							link: (
								node: { key: string },
								children: React.ReactNode,
								_: unknown,
								styles: { text: object },
							) => (
								<Text key={node.key} style={styles.text}>
									{children}
								</Text>
							),
						}}
						text={`You last logged in with the email **${lastLoggedInEmail}**`}
						textAlign="center"
						customStyles={{
							paragraph: {
								lineHeight: 22,
							},
						}}
					/>
				</Box>
			)}
			<Controller
				control={control}
				render={({ field: { onChange, onBlur, value } }) => (
					<Box>
						<TextInput
							mode="outlined"
							placeholder={t("auth.emailPlaceholder")}
							onChangeText={(text) => {
								onChange(text);
								onChangeEmail?.(text);
								setValue("password", "");
							}}
							onBlur={onBlur}
							value={value}
							autoFocus
							autoCapitalize="none"
							onSubmitEditing={
								showPasswordField
									? undefined
									: handleSubmit((data) =>
											handleSubmitEmail(data.email, data.password),
										)
							}
							keyboardType="email-address"
							disabled={loading}
							style={{ width: "100%" }}
							error={!!emailError}
						/>
						{emailError && <HelperText type="error">{emailError}</HelperText>}
					</Box>
				)}
				name="email"
				defaultValue=""
			/>
			{showPasswordField && (
				<Controller
					control={control}
					render={({ field: { onChange, onBlur, value } }) => (
						<Box>
							<TextInput
								ref={passwordInputRef}
								mode="outlined"
								placeholder={t("auth.passwordPlaceholder")}
								onChangeText={onChange}
								onBlur={onBlur}
								value={value}
								keyboardType="default"
								secureTextEntry={isPasswordHidden}
								disabled={loading}
								onSubmitEditing={handleSubmit((data) =>
									handleSubmitEmail(data.email, data.password),
								)}
								style={{ width: "100%" }}
								error={!!passwordError}
								right={
									<TextInput.Icon
										icon={isPasswordHidden ? "eye-outline" : "eye-off-outline"}
										disabled={loading}
										forceTextInputFocus={false}
										onPress={() => {
											setIsPasswordHidden((hidden) => {
												if (hidden) {
													queueMicrotask(() =>
														passwordInputRef.current?.blur(),
													);
												}
												return !hidden;
											});
										}}
									/>
								}
							/>
							{passwordError && (
								<HelperText type="error">{passwordError}</HelperText>
							)}
						</Box>
					)}
					name="password"
					defaultValue=""
				/>
			)}
			<Box gap={8}>
				<AuthButton
					onPress={handleSubmit((data) =>
						handleSubmitEmail(data.email, data.password),
					)}
					disabled={
						loading ||
						Boolean(errors.email) ||
						!watchEmail ||
						(showPasswordField && (!watchPassword || !!errors.password))
					}
					loading={loading}
				>
					Continue
				</AuthButton>
				<Footer />
			</Box>
		</Box>
	);
};

export default EmailForm;
